import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import DateRangePicker from '../../components/common/DateRangePicker';
import { usePagedData } from '../../hooks/usePagedData';
import { amazonCustomerInvoicesApi } from '../../api/endpoints';
import { AmazonCustomerInvoiceList, AmazonCustomerInvoiceSummary } from '../../types';
import Select from '../../components/common/Select';
import {
  FiRefreshCw, FiSearch, FiDownload, FiFileText, FiDollarSign, FiUsers, FiPercent,
} from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { toast } from 'react-toastify';

/* ------------------------------------------------------------------ */
/*  Transaction type badge                                             */
/* ------------------------------------------------------------------ */

const txTypeColors: Record<string, { color: string; bg: string }> = {
  SALE: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  REFUND: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  CREDIT_NOTE: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
};

const TxTypeBadge: React.FC<{ type: string | null }> = ({ type }) => {
  if (!type) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cfg = txTypeColors[type] || { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
      fontWeight: 600, color: cfg.color, backgroundColor: cfg.bg, whiteSpace: 'nowrap',
    }}>
      {type}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Stat card                                                          */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, iconColor, label, value }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderRadius: '10px',
    backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: iconBg, color: iconColor, fontSize: '16px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
        {value}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const AmazonCustomerInvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<AmazonCustomerInvoiceSummary | null>(null);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<AmazonCustomerInvoiceList>(amazonCustomerInvoicesApi.list, {
    initialSortBy: 'invoiceDate',
    initialSortDesc: true,
  });

  useEffect(() => {
    let cancelled = false;
    amazonCustomerInvoicesApi.summary()
      .then((resp) => { if (!cancelled) setSummary(resp.data); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [data]);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleTxTypeFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        transactionType: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleCountryFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        buyerCountry: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleDateFromChange = useCallback((value: string) => {
    setDateFrom(value);
    setFilters((prev: Record<string, any>) => ({ ...prev, dateFrom: value || undefined }));
  }, [setFilters]);

  const handleDateToChange = useCallback((value: string) => {
    setDateTo(value);
    setFilters((prev: Record<string, any>) => ({ ...prev, dateTo: value || undefined }));
  }, [setFilters]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await amazonCustomerInvoicesApi.sync();
      toast.success(t('customerInvoices.syncStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('customerInvoices.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh, t]);

  const handleDownloadPdf = useCallback(async (invoice: AmazonCustomerInvoiceList, e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloadingId(invoice.id);
    const toastId = toast.loading(
      invoice.hasCachedPdf
        ? t('customerInvoices.downloadingCached')
        : t('customerInvoices.downloadingFromAmazon'),
      { autoClose: false }
    );
    try {
      const response = await amazonCustomerInvoicesApi.downloadPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.update(toastId, {
        render: t('customerInvoices.downloadSuccess'),
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      refresh();
    } catch (error: any) {
      toast.update(toastId, {
        render: error.response?.data?.message || t('customerInvoices.downloadError'),
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setDownloadingId(null);
    }
  }, [t, refresh]);

  const formatCurrency = (amount: number | null, currency?: string | null) => {
    if (amount === null || amount === undefined) return '—';
    const cur = currency || 'EUR';
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: cur });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const countryOptions = useMemo(() => {
    const countries = Object.keys(summary?.byCountry || {}).sort();
    return [
      { value: '', label: t('customerInvoices.allCountries') },
      ...countries.map(c => ({ value: c, label: c })),
    ];
  }, [summary, t]);

  const columns = useMemo(() => [
    {
      key: 'invoiceDate',
      header: t('customerInvoices.invoiceDate'),
      sortable: true,
      render: (row: AmazonCustomerInvoiceList) => (
        <span style={{ fontSize: '13px', fontWeight: 600 }}>
          {formatDate(row.invoiceDate)}
        </span>
      ),
    },
    {
      key: 'invoiceNumber',
      header: t('customerInvoices.invoiceNumber'),
      sortable: true,
      render: (row: AmazonCustomerInvoiceList) => (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', fontWeight: 600 }}>
          {row.invoiceNumber}
        </span>
      ),
    },
    {
      key: 'transactionType',
      header: t('customerInvoices.type'),
      className: 'col-hide-mobile',
      render: (row: AmazonCustomerInvoiceList) => <TxTypeBadge type={row.transactionType} />,
    },
    {
      key: 'amazonOrderId',
      header: t('customerInvoices.orderId'),
      className: 'col-hide-mobile',
      render: (row: AmazonCustomerInvoiceList) => (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--text-muted)' }}>
          {row.amazonOrderId || '—'}
        </span>
      ),
    },
    {
      key: 'product',
      header: t('customerInvoices.product'),
      render: (row: AmazonCustomerInvoiceList) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={{
            fontSize: '12px', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px',
          }}>
            {row.productName || '—'}
          </span>
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
            {row.asin && <>ASIN: {row.asin}</>}
            {row.sku && <> · SKU: {row.sku}</>}
          </span>
        </div>
      ),
    },
    {
      key: 'buyer',
      header: t('customerInvoices.buyer'),
      className: 'col-hide-mobile',
      render: (row: AmazonCustomerInvoiceList) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '12px' }}>{row.buyerName || '—'}</span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{row.buyerCountry || ''}</span>
        </div>
      ),
    },
    {
      key: 'totalAmount',
      header: t('customerInvoices.total'),
      sortable: true,
      render: (row: AmazonCustomerInvoiceList) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>
            {formatCurrency(row.totalAmount, row.currency)}
          </span>
          {row.itemVatAmount !== null && (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              VAT {row.itemVatRate || 0}%: {formatCurrency(row.itemVatAmount, row.currency)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row: AmazonCustomerInvoiceList) => (
        <button
          className="btn btn-sm btn-secondary"
          onClick={(e) => handleDownloadPdf(row, e)}
          disabled={downloadingId === row.id}
          title={row.hasCachedPdf ? t('customerInvoices.pdfCached') : t('customerInvoices.pdfNotCached')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          <FiDownload />
          {downloadingId === row.id ? t('customerInvoices.downloading') : 'PDF'}
        </button>
      ),
    },
  ], [t, handleDownloadPdf, downloadingId]);

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('customerInvoices.title')}</h1>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('customerInvoices.syncing') : t('customerInvoices.syncWithAmazon')}
          </button>
        </div>
      </div>

      {summary && (
        <div className="po-stats-grid">
          <StatCard
            icon={<FiFileText />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3b82f6"
            label={t('customerInvoices.statTotalInvoices')}
            value={summary.totalInvoices}
          />
          <StatCard
            icon={<FiDollarSign />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('customerInvoices.statTotalGross')}
            value={formatCurrency(summary.totalGross, summary.currency)}
          />
          <StatCard
            icon={<FiPercent />}
            iconBg="rgba(245, 158, 11, 0.15)"
            iconColor="#f59e0b"
            label={t('customerInvoices.statTotalVat')}
            value={formatCurrency(summary.totalVat, summary.currency)}
          />
          <StatCard
            icon={<FiUsers />}
            iconBg="rgba(124, 58, 237, 0.15)"
            iconColor="#7c3aed"
            label={t('customerInvoices.statCountries')}
            value={Object.keys(summary.byCountry).length}
          />
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('customerInvoices.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('customerInvoices.allTypes') },
              { value: 'SALE', label: 'SALE' },
              { value: 'REFUND', label: 'REFUND' },
              { value: 'CREDIT_NOTE', label: 'CREDIT_NOTE' },
            ]}
            value={String(filters.transactionType ?? '')}
            onChange={handleTxTypeFilter}
            placeholder={t('customerInvoices.allTypes')}
          />

          <Select
            options={countryOptions}
            value={String(filters.buyerCountry ?? '')}
            onChange={handleCountryFilter}
            placeholder={t('customerInvoices.allCountries')}
          />
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={8} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
        />
      )}
    </div>
  );
};

export default AmazonCustomerInvoicesPage;
